import { Link } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import {
  AlertCircle,
  ChevronRight,
  FileCheck2,
  LoaderCircle,
  Plus,
  ShieldCheck,
  Trash2,
  UserCog,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useObraId } from "@/app/useObraId";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { DocumentoAcreditativoPanel } from "@/components/ui/DocumentoAcreditativoPanel";
import { DataTable } from "@/components/ui/data-table";
import { ExportButton } from "@/components/ui/ExportButton";
import { Modal } from "@/components/ui/modal";
import { canManageObra, ROL_AUTORIZADO, ROL_CUALIFICADO, useMe } from "@/lib/auth";
import {
  type Miembro,
  ROL_MIEMBRO_LABEL,
  useAsignarMiembro,
  useBorrarDocumentoMiembro,
  useDescargarDocumentoMiembro,
  useMiembros,
  useObras,
  useRetirarMiembro,
  useSetAutorizacion,
  useSubirDocumentoMiembro,
} from "@/lib/obras";
import { AutorizacionForm, type AutorizacionFormValues } from "./AutorizacionForm";
import { AvisosPanel } from "./AvisosPanel";
import { MiembroForm, type MiembroFormValues } from "./MiembroForm";

// limpio normaliza un texto opcional del formulario al payload del API (vacío ⇒ null).
function limpio(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

// nombrePersona compone el nombre visible de un miembro.
function nombrePersona(m: Miembro): string {
  return `${m.nombre} ${m.apellidos}`.trim();
}

// requiereDocumento espeja people.RequiereDocumentoAcreditativo (#435 D1): solo CUALIFICADO y
// AUTORIZADO necesitan la documentación acreditativa para operar.
function requiereDocumento(m: Miembro): boolean {
  return m.rol === ROL_CUALIFICADO || m.rol === ROL_AUTORIZADO;
}

/**
 * EstadoDocumento es la celda «Doc. acreditativa» (#435, R13): «Aportada», «Falta» —resaltada
 * SOLO si el rol del miembro la exige, porque entonces no puede operar— o «No requerida» para
 * los roles que no la necesitan.
 */
function EstadoDocumento({ miembro }: { miembro: Miembro }) {
  if (miembro.documento_acreditativo) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-0.5 font-medium text-green-800 text-sm">
        <FileCheck2 className="h-4 w-4" aria-hidden="true" />
        Aportada
      </span>
    );
  }
  if (requiereDocumento(miembro)) {
    return (
      <span
        data-resaltado="true"
        className="inline-flex rounded-full bg-red-100 px-2.5 py-0.5 font-semibold text-red-800 text-sm"
      >
        Falta
      </span>
    );
  }
  return (
    <span className="inline-flex rounded-full bg-slate-200 px-2.5 py-0.5 font-medium text-slate-600 text-sm">
      No requerida
    </span>
  );
}

/**
 * ConfiguracionObraPage es la pantalla de configuración de una obra (#238 D9): el alta
 * rápida de sus miembros con su rol y su autorización LOTO, y los destinatarios de los
 * avisos por correo, en una sola página sin pestañas. Sustituye a la antigua «Miembros y
 * autorizaciones»: la gestión completa de la pertenencia de una persona (todas sus obras a
 * la vez) vive ahora en su ficha, en `/usuarios`.
 *
 * Todo aquí es gestión de PERSONAS, así que comparte gate (`canManageObra`, espejo de
 * RequireObraAdmin): ADMIN global o ADMIN_OBRA de la obra; desde #255 (D2) el CUALIFICADO ya
 * no concede autorizaciones. El backend es el guardián real.
 */
export function ConfiguracionObraPage() {
  const { data: me } = useMe();
  const obraId = useObraId();

  const puedeGestionar = me != null && canManageObra(me, obraId);
  const esAdminGlobal = me?.es_admin ?? false;
  // El número de obras sale de `me.obras`, la misma fuente que usa el conmutador (#342 D4):
  // las dos superficies no pueden discrepar sobre cuántas obras tiene el usuario.
  const tieneVariasObras = (me?.obras.length ?? 0) > 1;

  // Nombre de la obra para el título y las migas: de la membresía si el usuario la tiene; si
  // no (un admin recién llegado a una obra que aún no está en su /me), de la colección.
  const enMe = me?.obras.find((o) => o.id_obra === obraId);
  const coleccion = useObras({ enabled: me != null && !enMe && esAdminGlobal });
  const nombreObra = enMe?.nombre ?? coleccion.data?.find((o) => o.id_obra === obraId)?.nombre;

  const miembros = useMiembros(obraId);

  const [altaMiembro, setAltaMiembro] = useState(false);
  const [miembroABorrar, setMiembroABorrar] = useState<Miembro | null>(null);
  const [autDe, setAutDe] = useState<Miembro | null>(null);
  // Miembro cuya documentación acreditativa se gestiona (#435). Se guarda el id y no la fila:
  // tras subir o borrar, el listado se refresca y el modal debe pintar el estado NUEVO.
  const [docDe, setDocDe] = useState<number | null>(null);
  const miembroDoc =
    docDe === null ? undefined : miembros.data?.find((m) => m.id_usuario === docDe);

  const asignar = useAsignarMiembro(obraId);
  const retirar = useRetirarMiembro(obraId);
  const setAut = useSetAutorizacion(obraId);
  const subirDoc = useSubirDocumentoMiembro(obraId);
  const borrarDoc = useBorrarDocumentoMiembro(obraId);
  const descargarDoc = useDescargarDocumentoMiembro(obraId);

  const columnas = useMemo<ColumnDef<Miembro, unknown>[]>(
    () => [
      {
        accessorKey: "nombre_usuario",
        header: "Usuario",
        cell: ({ row }) => (
          <span className="font-medium text-slate-900">{row.original.nombre_usuario}</span>
        ),
      },
      {
        id: "nombre",
        accessorFn: (m) => nombrePersona(m),
        header: "Nombre",
        cell: ({ getValue }) => <span className="text-slate-900">{getValue<string>()}</span>,
      },
      {
        id: "rol",
        accessorFn: (m) => ROL_MIEMBRO_LABEL[m.rol],
        header: "Rol",
        cell: ({ getValue }) => <span className="text-slate-500">{getValue<string>()}</span>,
      },
      {
        id: "autorizado_loto",
        header: "Autorizado LOTO",
        cell: ({ row }) =>
          row.original.autorizacion?.autorizado_loto && row.original.autorizacion.vigente ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-0.5 font-medium text-green-800 text-sm">
              <ShieldCheck className="h-4 w-4" aria-hidden="true" />
              Sí
            </span>
          ) : (
            <span className="inline-flex rounded-full bg-slate-200 px-2.5 py-0.5 font-medium text-slate-600 text-sm">
              No
            </span>
          ),
      },
      {
        id: "caducidad",
        accessorFn: (m) => m.autorizacion?.fecha_caducidad ?? "—",
        header: "Caducidad",
        cell: ({ getValue }) => <span className="text-slate-500">{getValue<string>()}</span>,
        meta: { className: "hidden sm:table-cell" },
      },
      {
        id: "documento_acreditativo",
        header: "Doc. acreditativa",
        enableSorting: false,
        enableGlobalFilter: false,
        cell: ({ row }) => <EstadoDocumento miembro={row.original} />,
      },
      {
        id: "acciones",
        header: () => <span className="sr-only">Acciones</span>,
        enableSorting: false,
        enableGlobalFilter: false,
        meta: { className: "text-right", stopRowClick: true },
        cell: ({ row }) => (
          <div className="flex justify-end gap-1">
            <Button
              variant="ghost"
              size="icon"
              aria-label={`Autorización LOTO de ${nombrePersona(row.original)}`}
              onClick={() => setAutDe(row.original)}
            >
              <ShieldCheck className="h-5 w-5" aria-hidden="true" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              aria-label={`Documentación acreditativa de ${nombrePersona(row.original)}`}
              onClick={() => setDocDe(row.original.id_usuario)}
            >
              <FileCheck2 className="h-5 w-5" aria-hidden="true" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              aria-label={`Quitar a ${nombrePersona(row.original)}`}
              onClick={() => setMiembroABorrar(row.original)}
            >
              <Trash2 className="h-5 w-5 text-red-600" aria-hidden="true" />
            </Button>
          </div>
        ),
      },
    ],
    [],
  );

  async function onAsignarMiembro(values: MiembroFormValues) {
    await asignar.mutateAsync({ id_usuario: values.id_usuario, rol: values.rol });
    setAltaMiembro(false);
  }

  async function confirmarRetiro() {
    if (!miembroABorrar) {
      return;
    }
    await retirar.mutateAsync(miembroABorrar.id_usuario);
    setMiembroABorrar(null);
  }

  async function onGuardarAutorizacion(values: AutorizacionFormValues) {
    await setAut.mutateAsync({
      id_persona: values.id_persona,
      autorizado_loto: values.autorizado_loto,
      vigente: values.vigente,
      fecha_autorizacion: limpio(values.fecha_autorizacion),
      fecha_caducidad: limpio(values.fecha_caducidad),
      emitida_por: limpio(values.emitida_por),
      alcance: limpio(values.alcance),
      observaciones: limpio(values.observaciones),
    });
    setAutDe(null);
  }

  // Guard de la UI (el guard de ruta ya redirige a quien no gestiona la obra): sin permiso
  // de gestión no se muestra nada accionable.
  if (me && !puedeGestionar) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
        <span>No tienes permiso para gestionar esta obra.</span>
      </Alert>
    );
  }

  // Migas contextuales por rol: el admin global gestiona la colección, así que su primer tramo
  // es «Gestión de obras» → /gestion/obras; el gestor de obra (ADMIN_OBRA) no la gestiona, así
  // que el suyo es «Obras» → el selector /obras. Con una sola obra esa miga NO se pinta
  // (#342 D1): una miga de pan es navegación de VUELTA, no una petición de «enséñame el
  // selector», y con una obra el selector auto-entra en ella (#252/#265), así que no tendría
  // destino útil. Sin primer tramo tampoco hay separador: la miga empieza en la obra.
  const migaDelSelector = tieneVariasObras ? (
    <Link to="/obras" className="text-primary-700 hover:underline">
      Obras
    </Link>
  ) : null;
  const primerTramo = esAdminGlobal ? (
    <Link to="/gestion/obras" className="text-primary-700 hover:underline">
      Gestión de obras
    </Link>
  ) : (
    migaDelSelector
  );

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <nav
          aria-label="Ruta de navegación"
          className="flex items-center gap-1 text-slate-500 text-sm"
        >
          {primerTramo !== null ? (
            <>
              {primerTramo}
              <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
            </>
          ) : null}
          <span className="truncate font-medium text-slate-700" aria-current="page">
            {nombreObra ?? "Obra"}
          </span>
        </nav>
        <h1 className="font-bold text-2xl text-slate-900 tracking-tight">
          {nombreObra ? `${nombreObra} · Configuración de obra` : "Configuración de obra"}
        </h1>
        <p className="text-base text-slate-500">
          Alta rápida de miembros de esta obra y destinatarios de sus avisos.
        </p>
      </div>

      {/* --- Miembros y autorizaciones --- */}
      <section className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-semibold text-lg text-slate-900">Miembros y autorizaciones</h2>
          <div className="flex flex-wrap justify-end gap-2">
            {/* Exportaciones de PERSONAS de la obra (#249 D3/D9): llevan datos personales,
                así que su ruta exige RequireObraAdmin —el mismo gate que esta pantalla—. Las
                dos secciones de D9 conviven aquí porque miembros y autorizaciones se
                gestionan en la misma tabla (#238 D9), de ahí las etiquetas explícitas. */}
            <ExportButton tipo="miembros" obraId={obraId} etiqueta="Exportar miembros" />
            <ExportButton
              tipo="autorizaciones"
              obraId={obraId}
              etiqueta="Exportar autorizaciones"
            />
            {esAdminGlobal ? (
              <Button variant="outline" asChild>
                <Link to="/usuarios">
                  <UserCog className="h-5 w-5" aria-hidden="true" />
                  Gestionar usuarios
                </Link>
              </Button>
            ) : null}
            <Button onClick={() => setAltaMiembro(true)}>
              <Plus className="h-5 w-5" aria-hidden="true" />
              Añadir miembro
            </Button>
          </div>
        </div>

        {miembros.isError ? (
          <Alert variant="destructive">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
            <span>No se pudieron cargar los miembros. Inténtalo de nuevo.</span>
          </Alert>
        ) : null}

        {miembros.isPending ? (
          <div className="flex items-center justify-center gap-2 py-10 text-slate-500">
            <LoaderCircle className="h-6 w-6 animate-spin" aria-hidden="true" />
            Cargando miembros…
          </div>
        ) : null}

        {miembros.data && miembros.data.length === 0 ? (
          <div className="rounded-2xl border border-slate-200 border-dashed bg-white px-4 py-12 text-center text-slate-500">
            Aún no hay miembros. Añade el primero con «Añadir miembro».
          </div>
        ) : null}

        {miembros.data && miembros.data.length > 0 ? (
          <DataTable columns={columnas} data={miembros.data} searchPlaceholder="Buscar miembros…" />
        ) : null}
      </section>

      {/* --- Avisos por correo --- */}
      <section className="flex flex-col gap-4">
        <h2 className="font-semibold text-lg text-slate-900">Avisos por correo</h2>
        <AvisosPanel obraId={obraId} miembros={miembros.data ?? []} />
      </section>

      {/* Alta de miembro */}
      <Modal open={altaMiembro} onClose={() => setAltaMiembro(false)} title="Añadir miembro">
        {altaMiembro ? (
          <MiembroForm
            obraId={obraId}
            pending={asignar.isPending}
            onSubmit={onAsignarMiembro}
            onCancel={() => setAltaMiembro(false)}
          />
        ) : null}
      </Modal>

      {/* Retirar miembro */}
      <Modal
        open={miembroABorrar !== null}
        onClose={() => setMiembroABorrar(null)}
        title="Quitar miembro"
      >
        <p className="text-base text-slate-600">
          ¿Seguro que quieres quitar a{" "}
          <strong>{miembroABorrar ? nombrePersona(miembroABorrar) : ""}</strong> de la obra? Su
          autorización LOTO se conserva como histórico, pero deja de aplicar: si vuelve a la obra
          habrá que autorizarla de nuevo.
        </p>
        {retirar.isError ? (
          <Alert variant="destructive">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
            <span>No se pudo quitar el miembro. Inténtalo de nuevo.</span>
          </Alert>
        ) : null}
        <div className="mt-4 flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => setMiembroABorrar(null)}
            disabled={retirar.isPending}
          >
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={() => void confirmarRetiro()}
            disabled={retirar.isPending}
          >
            {retirar.isPending ? (
              <LoaderCircle className="h-5 w-5 animate-spin" aria-hidden="true" />
            ) : (
              <Trash2 className="h-5 w-5" aria-hidden="true" />
            )}
            Quitar
          </Button>
        </div>
      </Modal>

      {/* Documentación acreditativa del miembro (#435): global a todas sus obras; aquí se
          gestiona contra la ruta del miembro de ESTA obra. */}
      <Modal
        open={docDe !== null}
        onClose={() => setDocDe(null)}
        title="Documentación acreditativa"
        className="max-w-xl"
      >
        {docDe !== null && miembroDoc ? (
          <div className="flex flex-col gap-3">
            <p className="text-base text-slate-600">
              <strong>{nombrePersona(miembroDoc)}</strong> · {ROL_MIEMBRO_LABEL[miembroDoc.rol]}.{" "}
              {requiereDocumento(miembroDoc)
                ? "Su rol la exige: sin ella no puede operar LOTOs."
                : "Su rol no la exige, pero vale para todas sus obras."}
            </p>
            <DocumentoAcreditativoPanel
              documento={miembroDoc.documento_acreditativo}
              onSubir={(archivo) => subirDoc.mutateAsync({ idUsuario: docDe, archivo })}
              onDescargar={() => descargarDoc.mutateAsync(docDe)}
              onBorrar={() => borrarDoc.mutateAsync(docDe)}
            />
          </div>
        ) : null}
      </Modal>

      {/* Autorización LOTO del miembro (la persona es fija: la fila desde la que se abrió) */}
      <Modal
        open={autDe !== null}
        onClose={() => setAutDe(null)}
        title="Editar autorización"
        className="max-w-2xl"
      >
        {autDe !== null ? (
          <AutorizacionForm
            mode="editar"
            personas={[]}
            personaFija={nombrePersona(autDe)}
            defaultValues={{
              id_persona: autDe.id_persona,
              autorizado_loto: autDe.autorizacion?.autorizado_loto ?? false,
              vigente: autDe.autorizacion?.vigente ?? false,
              fecha_autorizacion: autDe.autorizacion?.fecha_autorizacion ?? "",
              fecha_caducidad: autDe.autorizacion?.fecha_caducidad ?? "",
              emitida_por: autDe.autorizacion?.emitida_por ?? "",
              alcance: autDe.autorizacion?.alcance ?? "",
              observaciones: autDe.autorizacion?.observaciones ?? "",
            }}
            pending={setAut.isPending}
            onSubmit={onGuardarAutorizacion}
            onCancel={() => setAutDe(null)}
          />
        ) : null}
      </Modal>
    </div>
  );
}
